import { useCallback } from 'react'
import { SceneVideoUser } from 'video-conference-media'
import { Config } from 'common/config'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useSelector } from 'react-redux'
import { RootState } from 'common/redux'
import { VideoColumns } from 'room/group-room/presentation/video-columns'
import { ColumnLayout } from 'room/training-room/review-tasks/presentation/review-common'
import styled from '@emotion/styled'
import { getMultipleChoicePollingApi } from './multiple-choice-polling-api'
import { colors, MaybeUserProfile, ui } from '@beelday/common'
import MultipleChoiceForm from './multiple-choice-form'
import { unionBy } from 'lodash'
import useFetchMissingProfiles from 'users/use-fetch-missing-profiles'
import { useProfilesFor } from 'users/redux'
import { ArchivedActivityCard } from 'features/script'
import { navigateToRecaps } from 'workflow/navigate-to-recaps'
import { useSessionMode } from 'session-mode/use-session-mode'

const TwoColumnGrid = styled.div`
	display: flex;
	flex: 1;
	height: 100%;
	min-height: 0;
	width: 100%;
	margin-bottom: 15px;
`

const Container = styled.div`
	background-color: ${colors.white};
	width: 100%;
	height: 100%;
	box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.05);
	border-radius: 7px;
	margin-left: 20px;
`

/**
 * The trainer's own answer on the projector, in the side column that holds
 * participants' video online and is empty in person.
 */
const TrainerAnswer = styled(Container)`
	margin-left: 0;
`

/**
 * `$beside` lets the chart give up width to that column instead of pushing it
 * out. One component either way: swapping component types would remount the
 * chart - and replay its animation - the moment the trainer answers.
 */
const Results = styled(Container)<{ $beside: boolean; $alone: boolean }>`
	${props => (props.$beside ? 'min-width: 0;' : '')}
	${props => (props.$alone ? 'margin-left: 0;' : '')}
`

type Props = {
	streams: SceneVideoUser[]
	userProfile?: MaybeUserProfile
	isTrainer: boolean
}

const MultipleChoicePollingPage = ({
	streams,
	isTrainer,
	userProfile,
}: Props): JSX.Element | null => {
	const multipleChoicePollingApi = getMultipleChoicePollingApi(
		Config.beeldayBackendUrl
	)
	const roomAddress = useAssertedJoinedRoomAddress()
	const pollingDefinition = useSelector(
		(state: RootState) => state.multipleChoicePolling.pollingDefinition
	)
	const questionWithAnswers = useSelector(
		(state: RootState) => state.multipleChoicePolling.questionWithAnswers
	)
	const userAnswered = !!questionWithAnswers?.find(
		answer => answer.userId === userProfile?.id
	)
	const answers = useSelector(
		(state: RootState) => state.multipleChoicePolling.answerers
	)
	const allUsers = useSelector(
		(state: RootState) => state.multipleChoicePolling.allUsers
	)
	const training = useSelector((state: RootState) => state.sessionInfo.training)
	const inPerson = useSessionMode() === 'IN_PERSON'

	const userAnsweredList = unionBy(questionWithAnswers, 'userId')
	const userAnsweredListIds = userAnsweredList.map(user => user.userId)
	useFetchMissingProfiles(userAnsweredListIds)

	const answeredUserProfiles = useProfilesFor(answers)

	const onSubmit = useCallback(
		answerIds => {
			if (pollingDefinition) {
				multipleChoicePollingApi.sendAnswer(
					roomAddress,
					pollingDefinition.id,
					answerIds
				)
			}
		},
		[multipleChoicePollingApi, pollingDefinition, roomAddress]
	)

	if (!pollingDefinition) return null

	if (pollingDefinition.doneInPreviousSession) {
		return (
			<ArchivedActivityCard
				title={pollingDefinition.question}
				toolType="POLLING_MULTIPLE_CHOICE"
				isTrainer={isTrainer}
				onSeeInRecaps={
					training
						? () => navigateToRecaps({ trainingId: training.id, isTrainer })
						: undefined
				}
			/>
		)
	}

	const trainerMayAnswer =
		isTrainer && !userAnswered && pollingDefinition.letFacilitatorAnswer
	const answerBeside = trainerMayAnswer && inPerson
	/**
	 * A participant's laptop in person has no cameras to put beside the card:
	 * it takes the whole width the stage gives it, as the word cloud does.
	 */
	const videoColumn = isTrainer || !inPerson
	const form = (
		<MultipleChoiceForm
			pollingDefinition={pollingDefinition}
			onSubmit={onSubmit}
			userProfile={userProfile}
		/>
	)

	/*
	 * Online, a trainer who may answer sees the results once they have. In
	 * person the projector is the only screen the room reads results from, so
	 * they are there from the first answer, with the trainer's form beside them.
	 */
	return (
		<>
			<TwoColumnGrid>
				<Results $beside={answerBeside} $alone={!answerBeside && !videoColumn}>
					{trainerMayAnswer && !inPerson ? form : null}
					{!isTrainer && !userAnswered ? form : null}

					{(userAnswered && pollingDefinition && questionWithAnswers) ||
					(isTrainer &&
						(!pollingDefinition.letFacilitatorAnswer || inPerson)) ? (
						<ui.MultipleChoiceResults
							possibleAnswers={pollingDefinition.possibleAnswers}
							question={pollingDefinition.question}
							questionWithAnswers={questionWithAnswers}
							total={allUsers.length}
							answeredUserProfiles={answeredUserProfiles}
							userApiUrl={Config.USER_API_URL}
						/>
					) : null}
				</Results>
				{answerBeside || videoColumn ? (
					<ColumnLayout>
						{answerBeside ? (
							<TrainerAnswer data-testid="multiple-choice-trainer-answer">
								{form}
							</TrainerAnswer>
						) : (
							<VideoColumns sceneVideoUsers={streams} />
						)}
					</ColumnLayout>
				) : null}
			</TwoColumnGrid>
		</>
	)
}

export default MultipleChoicePollingPage
